import { dadataControllerSuggestAddress } from "@pro-sto/api-client";

export interface AddressSuggestion {
  label: string;
  lat: number;
  lon: number;
}

export const DEFAULT_MAP_CENTER = { lat: 59.9343, lon: 30.3351 };

/** Web Mercator tiles — совпадают с WGS84 координатами из приложения. Яндекс на Leaflet даёт сдвиг. */
export const HELP_MAP_TILE_URL =
  "https://tile2.maps.2gis.com/tiles?x={x}&y={y}&z={z}&v=1&r=g&ts=online_sd";

interface DadataSuggestionRaw {
  value?: string;
  data?: {
    geo_lat?: string | number | null;
    geo_lon?: string | number | null;
    city?: string | null;
    settlement?: string | null;
  };
}

interface DadataAddressResponse {
  suggestions?: DadataSuggestionRaw[];
}

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

let leafletLoadPromise: Promise<unknown> | undefined;

export function formatPointAddress(city: string, address: string): string {
  if (city && address) {
    const cityLabel = /^(г\.?|д\.?|п\.?|пос\.?|село)\s/i.test(city)
      ? city
      : `г. ${city}`;
    return `${cityLabel}, ${address}`;
  }
  return address || city || "";
}

export function parseAddressSuggestion(label: string): { city: string; address: string } {
  const cleaned = label.replace(/^Россия,\s*/i, "").trim();
  const cityMatch = cleaned.match(
    /^(?:г\.?\s*|д\.?\s*|п\.?\s*|пос\.?\s*|село\s+)?([^,]+),\s*(.+)$/i,
  );
  if (cityMatch) {
    return { city: cityMatch[1].trim(), address: cityMatch[2].trim() };
  }
  const parts = cleaned
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    return { city: parts[0], address: parts.slice(1).join(", ") };
  }
  return { city: "", address: cleaned };
}

export function effectivePointCoords(point: { lat: number; lon: number }) {
  if (point.lat && point.lon) {
    return { lat: point.lat, lon: point.lon };
  }
  return DEFAULT_MAP_CENTER;
}

/** Примерное расстояние в метрах (WGS84). */
export function coordsDistanceMeters(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  const earthRadius = 6_371_000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.min(1, Math.sqrt(h)));
}

export async function fetchAddressSuggestions(
  query: string,
): Promise<AddressSuggestion[]> {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];
  try {
    const result = await dadataControllerSuggestAddress({
      requestBody: { query: trimmed },
    });
    const raw = result as DadataAddressResponse | null;
    return (raw?.suggestions ?? [])
      .map((suggestion) => {
        const lat = Number(suggestion.data?.geo_lat);
        const lon = Number(suggestion.data?.geo_lon);
        if (!suggestion.value || !Number.isFinite(lat) || !Number.isFinite(lon)) {
          return null;
        }
        if (lat === 0 && lon === 0) return null;
        return { label: suggestion.value, lat, lon };
      })
      .filter((item): item is AddressSuggestion => item !== null);
  } catch (error) {
    console.warn("[Help] fetchAddressSuggestions failed", error);
    return [];
  }
}

/** Подсказка адреса после перетаскивания маркера — только DaData, без Nominatim. */
const REVERSE_GEOCODE_MAX_METERS = 500;

export async function reverseGeocode(lat: number, lon: number): Promise<string | null> {
  const coordQueries = [
    `${lat.toFixed(6)}, ${lon.toFixed(6)}`,
    `${lat.toFixed(6)} ${lon.toFixed(6)}`,
  ];

  for (const query of coordQueries) {
    const items = await fetchAddressSuggestions(query);
    const best = items[0];
    if (!best) continue;

    const meters = coordsDistanceMeters({ lat, lon }, { lat: best.lat, lon: best.lon });
    if (meters <= REVERSE_GEOCODE_MAX_METERS) {
      return best.label;
    }
  }

  console.warn("[Help] reverseGeocode: no DaData address near coordinates", {
    lat,
    lon,
  });
  return null;
}

export function loadLeaflet(): Promise<unknown> {
  const win = window as Window & { L?: unknown };
  if (win.L) return Promise.resolve(win.L);
  if (leafletLoadPromise) return leafletLoadPromise;

  leafletLoadPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }

    const existingScript = document.querySelector(
      `script[src="${LEAFLET_JS}"]`,
    ) as HTMLScriptElement | null;

    const handleLoad = () => resolve((window as Window & { L?: unknown }).L);
    const handleError = (event: Event | string) => {
      leafletLoadPromise = undefined;
      reject(event);
    };

    if (existingScript) {
      existingScript.addEventListener("load", handleLoad, { once: true });
      existingScript.addEventListener("error", handleError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = LEAFLET_JS;
    script.onload = handleLoad;
    script.onerror = handleError;
    document.head.appendChild(script);
  });

  return leafletLoadPromise;
}
